import { db } from '../../db/database';
import { notifyOutboxChanged } from '../../services/sync/syncEngine';
import type { ShiftRecord } from '../../db/types';

export type ShiftActor = { id: string; role: 'owner' | 'manager' | 'cashier'; deviceId: string };
export type ShiftReconciliation = {
  openingCashCentavos: number;
  cashSalesCentavos: number;
  cashRefundsCentavos: number;
  expectedCashCentavos: number;
  actualCashCentavos?: number;
  varianceCentavos?: number;
};

function assertCanClose(actor: ShiftActor, shift: ShiftRecord) {
  if (actor.role === 'cashier' && shift.cashierId !== actor.id)
    throw new Error('Cashiers may only close their own shift.');
  if (!['owner', 'manager', 'cashier'].includes(actor.role))
    throw new Error('You are not authorized to close a shift.');
}

export async function getOpenShift(cashierId: string) {
  return db.shifts
    .where('cashierId')
    .equals(cashierId)
    .filter((shift) => shift.status === 'OPEN')
    .first();
}
export async function listShiftHistory() {
  return db.shifts.orderBy('openedAt').reverse().toArray();
}

async function calculateCashSales(shift: ShiftRecord) {
  const end = shift.closedAt ?? new Date().toISOString();
  const orders = await db.orders.where('cashierId').equals(shift.cashierId).toArray();
  const orderIds = new Set(
    orders
      .filter(
        (order) =>
          order.createdAt >= shift.openedAt &&
          order.createdAt <= end &&
          order.orderStatus !== 'VOIDED',
      )
      .map((order) => order.id),
  );
  const payments = await db.payments.toArray();
  return payments
    .filter((payment) => payment.method === 'CASH' && orderIds.has(payment.orderId))
    .reduce((sum, payment) => sum + payment.amountCentavos, 0);
}

async function calculateCashRefunds() {
  // The existing project has no refund transaction/ledger workflow. Phase 10 stores and reports the field,
  // but does not invent refund amounts. Refunds remain zero until that workflow is implemented.
  return 0;
}

export async function getShiftReconciliation(shift: ShiftRecord): Promise<ShiftReconciliation> {
  const cashSalesCentavos = shift.cashSalesCentavos ?? (await calculateCashSales(shift));
  const cashRefundsCentavos = shift.cashRefundsCentavos ?? (await calculateCashRefunds());
  const expectedCashCentavos = shift.openingCashCentavos + cashSalesCentavos - cashRefundsCentavos;
  return {
    openingCashCentavos: shift.openingCashCentavos,
    cashSalesCentavos,
    cashRefundsCentavos,
    expectedCashCentavos,
    actualCashCentavos: shift.actualCashCentavos,
    varianceCentavos: shift.varianceCentavos,
  };
}

export async function openShift(input: {
  actor: ShiftActor;
  openingCashCentavos: number;
  now?: string;
  shiftId?: string;
}) {
  if (!Number.isInteger(input.openingCashCentavos) || input.openingCashCentavos < 0)
    throw new Error('Opening cash must be a non-negative whole-centavo amount.');
  const existing = await getOpenShift(input.actor.id);
  if (existing) throw new Error('An open shift already exists for this cashier.');
  const now = input.now ?? new Date().toISOString();
  const shift: ShiftRecord = {
    id: input.shiftId ?? crypto.randomUUID(),
    cashierId: input.actor.id,
    deviceId: input.actor.deviceId,
    openingCashCentavos: input.openingCashCentavos,
    status: 'OPEN',
    openedAt: now,
  };
  await db.transaction('rw', [db.shifts, db.auditLogs, db.outbox], async () => {
    await db.shifts.add(shift);
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'SHIFT_OPENED',
      entityType: 'SHIFT',
      entityId: shift.id,
      actorId: input.actor.id,
      deviceId: input.actor.deviceId,
      metadata: JSON.stringify({
        openingCashCentavos: input.openingCashCentavos,
        role: input.actor.role,
      }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'SHIFT_OPENED',
      aggregateType: 'SHIFT',
      aggregateId: shift.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify({ shift }),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
  return shift;
}

export async function closeShift(input: {
  shiftId: string;
  actor: ShiftActor;
  actualCashCentavos: number;
  now?: string;
}) {
  if (!Number.isInteger(input.actualCashCentavos) || input.actualCashCentavos < 0)
    throw new Error('Actual cash must be a non-negative whole-centavo amount.');
  const existing = await db.shifts.get(input.shiftId);
  if (!existing) throw new Error('Shift not found.');
  if (existing.status !== 'OPEN') throw new Error('Shift is already closed.');
  assertCanClose(input.actor, existing);
  const now = input.now ?? new Date().toISOString();
  const reconciliation = await getShiftReconciliation(existing);
  const varianceCentavos = input.actualCashCentavos - reconciliation.expectedCashCentavos;
  const closed: ShiftRecord = {
    ...existing,
    ...reconciliation,
    actualCashCentavos: input.actualCashCentavos,
    varianceCentavos,
    status: 'CLOSED',
    closedAt: now,
  };
  await db.transaction('rw', [db.shifts, db.auditLogs, db.outbox], async () => {
    await db.shifts.put(closed);
    await db.auditLogs.add({
      id: crypto.randomUUID(),
      action: 'SHIFT_CLOSED',
      entityType: 'SHIFT',
      entityId: closed.id,
      actorId: input.actor.id,
      deviceId: input.actor.deviceId,
      metadata: JSON.stringify({
        ...reconciliation,
        actualCashCentavos: input.actualCashCentavos,
        varianceCentavos,
        role: input.actor.role,
      }),
      createdAt: now,
    });
    await db.outbox.add({
      id: crypto.randomUUID(),
      eventType: 'SHIFT_CLOSED',
      aggregateType: 'SHIFT',
      aggregateId: closed.id,
      idempotencyKey: crypto.randomUUID(),
      payload: JSON.stringify({ shift: closed }),
      status: 'PENDING',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    });
  });
  notifyOutboxChanged();
  return closed;
}
